import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { Input, Avatar, Badge, Button, Empty, Spin, Tooltip, Dropdown } from "antd";
import {
  SearchOutlined, SendOutlined, PaperClipOutlined, SmileOutlined,
  MoreOutlined, PhoneOutlined, VideoCameraOutlined, DeleteOutlined,
  BlockOutlined, FlagOutlined, CheckCircleOutlined, LoadingOutlined,
  MessageOutlined, ArrowLeftOutlined, CloseOutlined, AudioOutlined,
  CameraOutlined, MinusOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { BASE_API_CONFIG } from "../util/Api";
import { isValidString } from "../util/ApiHelper";

const { TextArea } = Input;

const CHAT_BUSINESS_TYPE_ID = {
  1: "Business", 2: "Business ", 3: "Business ",
  4: "Business", 5: "Business",
};
const getBusinessType = (id) => CHAT_BUSINESS_TYPE_ID[id] || "Business";

/* ─── Injected Styles ─── */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&display=swap');

  .frl-chat-root * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }

  .frl-chat-root ::-webkit-scrollbar { width: 4px; }
  .frl-chat-root ::-webkit-scrollbar-track { background: transparent; }
  .frl-chat-root ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }

  /* Floating chat bubble */
  .frl-fab {
    position: fixed; bottom: 28px; right: 28px; z-index: 9999;
    width: 54px; height: 54px; border-radius: 50%;
    background: linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; box-shadow: 0 8px 24px rgba(29,78,216,0.38);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    border: none;
  }
  .frl-fab:hover { transform: scale(1.08); box-shadow: 0 12px 32px rgba(29,78,216,0.45); }

  /* Chat List Panel - FIXED POSITION, NEVER MOVES */
  .frl-list-panel {
    position: fixed;
    bottom: 96px;
    right: 28px;
    z-index: 9998;
    width: 320px;
    max-height: 480px;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.16), 0 4px 16px rgba(0,0,0,0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: frl-slide-up 0.22s cubic-bezier(0.34,1.56,0.64,1);
    transform-origin: bottom right;
  }
  @keyframes frl-slide-up {
    from { opacity: 0; transform: scale(0.88) translateY(12px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }

  /* Chat Detail Panel - Opens to the LEFT of chat list */
  .frl-detail-panel {
    position: fixed;
    bottom: 96px;
    right: 360px;
    z-index: 9997;
    width: 360px;
    max-height: 540px;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: frl-slide-in-left 0.24s cubic-bezier(0.34,1.56,0.64,1);
    transform-origin: right center;
  }
  @keyframes frl-slide-in-left {
    from { opacity: 0; transform: translateX(20px); }
    to   { opacity: 1; transform: translateX(0); }
  }

  .frl-detail-panel.frl-minimized {
    max-height: 52px !important;
  }

  @media (max-width: 900px) {
    .frl-list-panel {
      right: 16px;
      bottom: 88px;
      width: min(320px, calc(100vw - 32px));
    }

    .frl-detail-panel {
      right: 16px;
      bottom: 88px;
      width: min(360px, calc(100vw - 32px));
      max-width: calc(100vw - 32px);
      z-index: 9999;
    }
  }

  /* Chat list header */
  .frl-list-header {
    padding: 16px 16px 10px;
    border-bottom: 1px solid #f1f5f9;
    background: #ffffff;
  }
  .frl-list-header-top {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 10px;
  }
  .frl-list-title {
    font-size: 16px; font-weight: 600; color: #0f172a; margin: 0;
  }
  .frl-search {
    border-radius: 10px !important; border: 1.5px solid #e2e8f0 !important;
    background: #f8fafc !important; font-size: 13px !important;
    height: 36px;
  }
  .frl-search:focus-within { border-color: #1d4ed8 !important; }

  /* Chat items */
  .frl-chat-items { flex: 1; overflow-y: auto; }
  .frl-chat-item {
    display: flex; align-items: center; gap: 10px;
    padding: 11px 14px; cursor: pointer;
    border-bottom: 1px solid #f8fafc;
    transition: background 0.15s ease;
  }
  .frl-chat-item:hover { background: #f8fafc; }
  .frl-chat-item.active { background: #eff6ff; }
  .frl-chat-item-info { flex: 1; min-width: 0; }
  .frl-chat-item-top {
    display: flex; justify-content: space-between; align-items: baseline;
  }
  .frl-chat-name {
    font-size: 13.5px; font-weight: 600; color: #0f172a;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    max-width: 160px;
  }
  .frl-chat-time { font-size: 10.5px; color: #94a3b8; flex-shrink: 0; }
  .frl-chat-preview {
    font-size: 12px; color: #64748b; margin-top: 2px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .frl-chat-preview.unread { color: #0f172a; font-weight: 500; }

  /* Detail header */
  .frl-detail-header {
    padding: 12px 14px;
    background: linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%);
    display: flex; align-items: center; gap: 10px;
  }
  .frl-detail-name { font-size: 14px; font-weight: 600; color: #fff; }
  .frl-detail-sub { font-size: 11px; color: rgba(255,255,255,0.75); }
  .frl-detail-actions { display: flex; gap: 4px; margin-left: auto; }
  .frl-icon-btn {
    background: rgba(255,255,255,0.15) !important;
    border: none !important; color: #fff !important;
    width: 30px !important; height: 30px !important;
    border-radius: 8px !important;
    display: flex !important; align-items: center !important; justify-content: center !important;
    transition: background 0.15s ease !important;
  }
  .frl-icon-btn:hover { background: rgba(255,255,255,0.25) !important; }

  /* Messages */
  .frl-messages-area {
    flex: 1; overflow-y: auto; padding: 14px 14px 8px;
    background: #f8fafc;
    background-image: radial-gradient(#e2e8f0 1px, transparent 1px);
    background-size: 20px 20px;
  }
  .frl-bubble-wrap {
    display: flex; margin-bottom: 8px;
  }
  .frl-bubble-wrap.own { justify-content: flex-end; }
  .frl-bubble {
    max-width: 72%; border-radius: 14px; padding: 8px 12px;
    font-size: 13px; line-height: 1.5; color: #0f172a;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  }
  .frl-bubble.theirs {
    background: #ffffff; border-bottom-left-radius: 4px;
  }
  .frl-bubble.mine {
    background: linear-gradient(135deg, #1d4ed8, #7c3aed);
    color: #ffffff; border-bottom-right-radius: 4px;
  }
  .frl-bubble-time {
    font-size: 10px; margin-top: 4px; text-align: right; opacity: 0.65;
  }

  /* Input area */
  .frl-input-area {
    padding: 10px 12px;
    background: #ffffff; border-top: 1px solid #f1f5f9;
    display: flex; align-items: flex-end; gap: 8px;
  }
  .frl-textarea {
    flex: 1; border-radius: 12px !important; resize: none !important;
    border: 1.5px solid #e2e8f0 !important; font-size: 13px !important;
    background: #f8fafc !important; padding: 8px 12px !important;
    max-height: 100px;
  }
  .frl-textarea:focus { border-color: #1d4ed8 !important; background: #fff !important; }
  .frl-send-btn {
    width: 36px !important; height: 36px !important;
    border-radius: 10px !important; border: none !important;
    background: linear-gradient(135deg, #1d4ed8, #7c3aed) !important;
    display: flex !important; align-items: center !important; justify-content: center !important;
    flex-shrink: 0; cursor: pointer;
    transition: opacity 0.15s ease !important;
  }
  .frl-send-btn:disabled { opacity: 0.45 !important; cursor: not-allowed !important; }
  .frl-send-btn svg { color: #fff !important; font-size: 14px !important; }

  .frl-empty {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 40px 20px; color: #94a3b8;
    font-size: 13px; gap: 8px;
  }
`;

/* ─── Helpers ─── */
const getInitials = (name) => {
  if (!name?.trim()) return "?";
  const w = name.trim().split(" ");
  return w.length === 1
    ? w[0].substring(0, 2).toUpperCase()
    : (w[0][0] + w[1][0]).toUpperCase();
};

const formatTimestamp = (ts) => {
  if (!ts) return "";
  const d = new Date(ts), now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const md = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (md.getTime() === today.getTime())
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  if (md.getTime() === today.getTime() - 86400000) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatIndianTime = (ts) => {
  if (!ts) return "";
  try {
    const date = new Date(ts);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", 
      hour: "numeric", 
      minute: "numeric", 
      hour12: true,
    });
  } catch (error) {
    console.error("Date formatting error:", error);
    return "";
  }
};

/* ─── Avatar colors pool ─── */
const AVATAR_COLORS = [
  "#1d4ed8", "#7c3aed", "#059669", "#d97706", "#db2777", "#0891b2",
];
const avatarColor = (name) =>
  AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

const getConversationKey = (chat) => {
  if (!chat) return null;

  const parts = [];

  if (chat.quotationId) parts.push(`quotation-${chat.quotationId}`);
  if (chat.enquiryId) parts.push(`enquiry-${chat.enquiryId}`);
  if (chat.promotionId) parts.push(`promotion-${chat.promotionId}`);

  if (parts.length > 0) {
    return parts.join("__");
  }

  return `direct-${Math.min(chat.senderId, chat.receiverId)}-${Math.max(chat.senderId, chat.receiverId)}`;
};

const getChatCounterparty = (chat, userId) => {
  if (!chat) return {};

  const senderId = Number(chat.senderId);
  const receiverId = Number(chat.receiverId);
  const currentUserId = Number(userId);
  const isReceiver = receiverId === currentUserId;
  const otherUser = isReceiver ? chat.sender : chat.receiver;

  return {
    id: isReceiver ? senderId : receiverId,
    name: otherUser?.companyName || otherUser?.name || "User",
    logo: otherUser?.companyLogo || "",
    businessType: otherUser?.businessType || otherUser?.businessTypeId,
  };
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const ChatUI = () => {
  const authUser = useSelector(({ auth }) => auth.userData);
  const userId = authUser?.id;
  const normalizedUserId = Number(authUser?.id);
  const token = authUser?.token;

  const [chatList, setChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sendMessage, setSendMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [listOpen, setListOpen] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const messagesEndRef = useRef(null);

  /* ── Fetch chat list ── */
  const fetchChatList = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_API_CONFIG.baseURL}/messages/chat-list/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const chatMap = new Map();
      response.data.data.forEach((chat) => {
        const counterparty = getChatCounterparty(chat, userId);
        const messages = [...(chat.messages || [])].sort(
          (a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt)
        );
        const latest = messages[0] || null;
        const unread = messages.filter((m) => !m.isread && Number(m.senderId) !== normalizedUserId).length;
        const key = getConversationKey(chat);
        const fmt = {
          ...chat,
          businessType: getBusinessType(counterparty.businessType),
          lastMessageTimestamp: latest ? new Date(latest.timestamp || latest.createdAt).getTime() : 0,
          lastMessageText: latest?.text || "",
          latestMessageTimestamp: latest?.timestamp || latest?.createdAt || null,
          unreadCount: unread,
          displayName: counterparty.name,
          displayLogo: counterparty.logo,
          displayId: counterparty.id,
        };
        if (!chatMap.has(key) || chatMap.get(key).lastMessageTimestamp < fmt.lastMessageTimestamp) {
          chatMap.set(key, fmt);
        }
      });
      const list = Array.from(chatMap.values()).sort(
        (a, b) => b.lastMessageTimestamp - a.lastMessageTimestamp
      );
      setChatList(list);
      setUnreadCount(list.reduce((s, c) => s + (c.unreadCount || 0), 0));
      setSelectedChat((prevSelectedChat) => {
        if (!prevSelectedChat) return prevSelectedChat;
        return list.find((chat) => getConversationKey(chat) === getConversationKey(prevSelectedChat)) || null;
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  /* ── Fetch messages ── FIXED ENDPOINT ── */
  const fetchChatMessages = useCallback(async (chat) => {
    if (!chat?.enquiryId && !chat?.quotationId && !chat?.promotionId) return;
    try {
      const fallbackMessages = [...(chat.messages || [])]
        .map(msg => ({
          ...msg,
          timestamp: msg.timestamp || msg.createdAt,
        }))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      // Existing app services use quotationId / promotionId for fetching threads.
      const params = {};
      if (chat.quotationId) {
        params.quotationId = chat.quotationId;
      } else if (chat.promotionId) {
        params.promotionId = chat.promotionId;
      } else if (chat.enquiryId) {
        params.enquiryId = chat.enquiryId;
      }

      const res = await axios.get(
        `${BASE_API_CONFIG.baseURL}/messages`,
        { 
          params,
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      
      // Normalize messages to ensure they have a timestamp field
      const normalizedMessages = (res.data.data || [])
        .map(msg => ({
          ...msg,
          timestamp: msg.timestamp || msg.createdAt,
        }))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      
      setSelectedChat({
        ...chat,
        messages: normalizedMessages.length > 0 ? normalizedMessages : fallbackMessages,
      });
      
      if (chat.unreadCount > 0 && chat.quotationId) {
        await axios.post(
          `${BASE_API_CONFIG.baseURL}/messages/mark-as-read/${chat.quotationId}`,
          {}, 
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update chat list immediately
        const conversationKey = getConversationKey(chat);
        setChatList(prevList => 
          prevList.map(item => 
            getConversationKey(item) === conversationKey 
              ? { ...item, unreadCount: 0 }
              : item
          )
        );
        
        setUnreadCount(prev => Math.max(0, prev - (chat.unreadCount || 0)));
      }
    } catch (e) {
      console.error("Error fetching messages:", e);
      const fallbackMessages = [...(chat.messages || [])]
        .map(msg => ({
          ...msg,
          timestamp: msg.timestamp || msg.createdAt,
        }))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setSelectedChat({ ...chat, messages: fallbackMessages });
    }
  }, [token]);

  /* ── Send message ── */
  const handleSend = async () => {
    if (!sendMessage.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${BASE_API_CONFIG.baseURL}/messages`,
        {
          text: sendMessage, senderId: normalizedUserId, sender: authUser?.name,
          receiverId: selectedChat.displayId, receiver: selectedChat.displayName,
          enquiryId: selectedChat.enquiryId, quotationId: selectedChat.quotationId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status === 200) {
        const newMessage = {
          text: sendMessage, 
          senderId: normalizedUserId, 
          timestamp: new Date().toISOString(),
          isread: false
        };
        
        setSelectedChat((prev) => ({
          ...prev,
          messages: [...(prev.messages || []), newMessage],
          lastMessageText: sendMessage,
          latestMessageTimestamp: new Date().toISOString(),
        }));
        setSendMessage("");
        fetchChatList();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  /* ── Select chat ── */
  const handleChatSelect = (chat) => {
    setSelectedChat(chat);
    fetchChatMessages(chat);
    setDetailOpen(true);
    setMinimized(false);
  };

  /* ── Close detail ── */
  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedChat(null);
  };

  /* ── Toggle list ── */
  const toggleList = () => {
    setListOpen((prev) => {
      if (prev) {
        setDetailOpen(false);
        setSelectedChat(null);
      }
      return !prev;
    });
  };

  /* ── Scroll ── */
  useEffect(() => {
    if (!minimized) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedChat?.messages, minimized]);

  useEffect(() => {
    if (!selectedChat) {
      setDetailOpen(false);
    }
  }, [selectedChat]);

  /* ── Init & poll ── */
  useEffect(() => { if (userId) fetchChatList(); }, [userId, fetchChatList]);
  useEffect(() => {
    if (!userId) return;
    const iv = setInterval(fetchChatList, 30000);
    return () => clearInterval(iv);
  }, [userId, fetchChatList]);

  const filtered = chatList.filter((c) =>
    (c.displayName || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const dropItems = [
    { key: "delete", label: "Delete conversation", icon: <DeleteOutlined />, danger: true },
    { key: "block", label: "Block user", icon: <BlockOutlined /> },
    { key: "report", label: "Report", icon: <FlagOutlined /> },
  ];

  return (
    <>
      <style>{styles}</style>

      <div className="frl-chat-root">
        {listOpen && (
          <div className="frl-list-panel">
            <div className="frl-list-header">
              <div className="frl-list-header-top">
                <span className="frl-list-title">Messages</span>
                <Button
                  type="text" size="small"
                  icon={<CloseOutlined style={{ fontSize: 13, color: "#64748b" }} />}
                  onClick={() => toggleList()}
                  style={{ border: "none", padding: "4px 6px" }}
                />
              </div>
              <Input
                className="frl-search"
                prefix={<SearchOutlined style={{ color: "#94a3b8", fontSize: 12 }} />}
                placeholder="Search conversations…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                allowClear
                size="small"
              />
            </div>

            <div className="frl-chat-items">
              {loading && chatList.length === 0 ? (
                <div className="frl-empty"><Spin size="small" /><span>Loading…</span></div>
              ) : filtered.length === 0 ? (
                <div className="frl-empty">
                  <MessageOutlined style={{ fontSize: 28, opacity: 0.3 }} />
                  <span>No conversations yet</span>
                </div>
              ) : (
                filtered.map((chat) => (
                  <div
                    key={getConversationKey(chat)}
                    className={`frl-chat-item${getConversationKey(selectedChat) === getConversationKey(chat) && detailOpen ? " active" : ""}`}
                    onClick={() => handleChatSelect(chat)}
                  >
                    <Badge dot={chat.unreadCount > 0} offset={[-4, 4]}
                      styles={{ indicator: { backgroundColor: "#22c55e", width: 9, height: 9 } }}>
                      <Avatar
                        size={40}
                        src={chat.displayLogo}
                        style={{ backgroundColor: avatarColor(chat.displayName), flexShrink: 0 }}
                      >
                        {getInitials(chat.displayName)}
                      </Avatar>
                    </Badge>
                    <div className="frl-chat-item-info">
                      <div className="frl-chat-item-top">
                        <span className="frl-chat-name">{chat.displayName}</span>
                        <span className="frl-chat-time">{formatTimestamp(chat.latestMessageTimestamp)}</span>
                      </div>
                      <div className={`frl-chat-preview${chat.unreadCount > 0 ? " unread" : ""}`}>
                        {chat.lastMessageText || "Tap to open conversation"}
                      </div>
                    </div>
                    {chat.unreadCount > 0 && (
                      <Badge count={chat.unreadCount}
                        style={{ backgroundColor: "#1d4ed8", fontSize: 10 }} />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {detailOpen && selectedChat && (
          <div className={`frl-detail-panel${minimized ? " frl-minimized" : ""}`}>
            <div className="frl-detail-header">
              <Avatar
                size={36}
                src={selectedChat.displayLogo}
                style={{ backgroundColor: avatarColor(selectedChat.displayName), flexShrink: 0 }}
              >
                {getInitials(selectedChat.displayName)}
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="frl-detail-name" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {selectedChat.displayName}
                </div>
                <div className="frl-detail-sub">{selectedChat.businessType}</div>
              </div>
              <div className="frl-detail-actions">
                <Tooltip title="Voice call">
                  <Button className="frl-icon-btn" type="text" size="small"
                    icon={<PhoneOutlined style={{ fontSize: 13 }} />} />
                </Tooltip>
                <Dropdown menu={{ items: dropItems }} trigger={["click"]}>
                  <Button className="frl-icon-btn" type="text" size="small"
                    icon={<MoreOutlined style={{ fontSize: 13 }} />} />
                </Dropdown>
                <Button className="frl-icon-btn" type="text" size="small"
                  icon={<MinusOutlined style={{ fontSize: 13 }} />}
                  onClick={() => setMinimized((p) => !p)} />
                <Button className="frl-icon-btn" type="text" size="small"
                  icon={<CloseOutlined style={{ fontSize: 13 }} />}
                  onClick={closeDetail} />
              </div>
            </div>

            {!minimized && (
              <>
                <div className="frl-messages-area">
                  {(selectedChat.messages || []).length === 0 ? (
                    <div className="frl-empty" style={{ height: "100%" }}>
                      <MessageOutlined style={{ fontSize: 24, opacity: 0.25 }} />
                      <span>No messages yet. Say hello!</span>
                    </div>
                  ) : (
                    (selectedChat.messages || []).map((msg, i) => {
                      const mine = Number(msg.senderId) === normalizedUserId;
                      return (
                        <div key={i} className={`frl-bubble-wrap${mine ? " own" : ""}`}>
                          <div className={`frl-bubble${mine ? " mine" : " theirs"}`}>
                            {msg.text}
                            <div className="frl-bubble-time">
                              {formatIndianTime(msg.timestamp)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  {sending && (
                    <div className="frl-bubble-wrap">
                      <div className="frl-bubble theirs" style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <LoadingOutlined style={{ color: "#7c3aed", fontSize: 12 }} spin />
                        <span style={{ fontSize: 12, color: "#94a3b8" }}>Typing…</span>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="frl-input-area">
                  <Tooltip title="Attach file">
                    <Button type="text" size="small"
                      icon={<PaperClipOutlined style={{ fontSize: 16, color: "#94a3b8" }} />}
                      style={{ border: "none", padding: "4px" }} />
                  </Tooltip>
                  <TextArea
                    className="frl-textarea"
                    value={sendMessage}
                    onChange={(e) => setSendMessage(e.target.value)}
                    onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Type a message…"
                    autoSize={{ minRows: 1, maxRows: 4 }}
                  />
                  <button
                    className="frl-send-btn"
                    onClick={handleSend}
                    disabled={!sendMessage.trim() || sending}
                  >
                    <SendOutlined />
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <button className="frl-fab" onClick={toggleList}>
          {listOpen
            ? <CloseOutlined style={{ color: "#fff", fontSize: 20 }} />
            : (
              <Badge count={unreadCount} offset={[8, -8]}
                styles={{ indicator: { backgroundColor: "#22c55e" } }}>
                <MessageOutlined style={{ color: "#fff", fontSize: 22 }} />
              </Badge>
            )
          }
        </button>
      </div>
    </>
  );
};

export default ChatUI;
